import {skipToken} from '@reduxjs/toolkit/query';
import {
  Action,
  Alert,
  BreadcrumbType,
  openConfirmDialog,
  openDeleteDialog,
  showNotification,
} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {useParams} from 'react-router-dom';

import {isNil, isNotNil} from 'ramda';

import {
  OAuthClientResponseBody,
  useDeleteOAuthClientMutation,
  useGetOAuthClientQuery,
  useRegenerateOAuthClientSecretMutation,
  useUpdateOAuthClientMutation,
} from '@dms/api/security';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {handleApiError, usePermissions} from '@dms/shared';

import {buildArray, useNavigate} from 'shared/web';

import {generateOAuthClientSecret} from '../utils/generateOAuthClientSecret';
import {OAuthClientForm, OAuthClientFormValues} from './OAuthClientForm';
import {OAuthClientSecretCard} from './OAuthClientSecretCard';

const breadcrumbs: BreadcrumbType[] = [
  {
    label: i18n.t('entity.oauthClient.labels.oauthClients'),
    href: settingsRoutes.oauthClientManagement,
  },
];

const getDefaultValues = (oauthClient: OAuthClientResponseBody): OAuthClientFormValues => ({
  clientType: isNotNil(oauthClient.clientSecret) ? 'confidential' : 'public',
  name: oauthClient.name,
  clientId: oauthClient.clientId,
  redirectUris: oauthClient.redirectUris ?? [],
});

export function OAuthClientDetail() {
  const navigate = useNavigate();
  const {oauthClientId} = useParams<{oauthClientId: string}>();

  const {
    data: oauthClient,
    isLoading,
    isError,
  } = useGetOAuthClientQuery(isNotNil(oauthClientId) ? {oauthClientId} : skipToken);

  const [updateOAuthClient] = useUpdateOAuthClientMutation();
  const [deleteOAuthClient] = useDeleteOAuthClientMutation();
  const [regenerateOAuthClientSecret] = useRegenerateOAuthClientSecretMutation();

  const [canUpdate, canDelete, canRegenerate] = usePermissions({
    permissionKeys: ['updateOAuthClient', 'deleteOAuthClient', 'regenerateOAuthClientSecret'],
  });

  const isCustomClient = oauthClient?.custom ?? false;
  const clientSecret = oauthClient?.clientSecret;

  const handleSubmit = async (values: OAuthClientFormValues) => {
    if (isNil(oauthClient)) {
      return;
    }

    await updateOAuthClient({
      oauthClientId: oauthClient.id,
      updateOAuthClientRequestBody: {
        name: values.name,
        clientId: values.clientId,
        redirectUris: values.redirectUris,
      },
    })
      .unwrap()
      .then(() => {
        showNotification.success();
        navigate(settingsRoutes.oauthClientManagement);
      })
      .catch(handleApiError);
  };

  const handleRegenerateSecret = (id: string) =>
    openConfirmDialog({
      text: i18n.t('entity.oauthClient.labels.regenerateSecretConfirm'),
      'data-testid': testIds.settings.oauthClientManagementDetail('regenerateSecretConfirm'),
      onConfirm: () =>
        regenerateOAuthClientSecret({
          oauthClientId: id,
          regenerateOAuthClientSecretRequestBody: {
            clientSecret: generateOAuthClientSecret(),
          },
        })
          .unwrap()
          .then(() =>
            showNotification.success(
              i18n.t('entity.oauthClient.labels.secretRegeneratedNotification')
            )
          )
          .catch(handleApiError),
    });

  const handleDelete = (id: string, name: string) =>
    openDeleteDialog({
      text: i18n.t('entity.oauthClient.labels.deleteDialog', {name}),
      'data-testid': testIds.settings.oauthClientManagementDetail('deleteDialog'),
      onConfirm: () =>
        deleteOAuthClient({oauthClientId: id})
          .unwrap()
          .then(() => navigate(settingsRoutes.oauthClientManagement))
          .catch(handleApiError),
    });

  const deletableClient = isCustomClient && canDelete ? oauthClient : undefined;

  const actions = buildArray<Action>().whenNotNil(deletableClient, (client) => ({
    type: 'button',
    variant: 'danger',
    leftIcon: 'action/delete',
    title: i18n.t('general.actions.delete'),
    onClick: () => handleDelete(client.id, client.name),
    'data-testid': testIds.settings.oauthClientManagementDetail('delete'),
  }));

  return (
    <SettingsTemplate
      isLoading={isLoading}
      isError={isError}
      header={{title: oauthClient?.name, breadcrumbs, actions}}
      data-testid={testIds.settings.oauthClientManagementDetail('page')}
    >
      <Show when={oauthClient}>
        {(oauthClient) => (
          <VStack spacing={4}>
            <Show when={!isCustomClient}>
              <Alert
                variant="info"
                title={i18n.t('entity.oauthClient.labels.systemClientInfo')}
                data-testid={testIds.settings.oauthClientManagementDetail('systemClientInfo')}
              />
            </Show>
            <Show when={isCustomClient && clientSecret}>
              {(clientSecret) => (
                <OAuthClientSecretCard
                  clientSecret={clientSecret}
                  canRegenerate={canRegenerate}
                  onRegenerate={() => handleRegenerateSecret(oauthClient.id)}
                  data-testid={testIds.settings.oauthClientManagementDetail('clientSecret')}
                />
              )}
            </Show>
            <OAuthClientForm
              mode="edit"
              isReadOnly={!isCustomClient || !canUpdate}
              isClientTypeHidden={!isCustomClient}
              defaultValues={getDefaultValues(oauthClient)}
              submitButtonTitle={i18n.t('general.actions.saveChanges')}
              onSubmit={handleSubmit}
              onCancel={() => navigate(settingsRoutes.oauthClientManagement)}
              data-testid={testIds.settings.oauthClientManagementDetail('form')}
            />
          </VStack>
        )}
      </Show>
    </SettingsTemplate>
  );
}
